import { apiRequest } from '../../../utils/api';
import { groupSimilarKeywords } from '../../../utils/KeywordGrouping';
import md5 from 'md5';

/**
 * Transform analysis object to keywords array for clustering
 * @param {Object} analysis - The analysis data object
 * @param {Function} setProcessingStage - Function to update processing stage
 * @returns {Array} Array of keywords formatted for clustering
 */
export function extractKeywordsForClustering(analysis, setProcessingStage) {
  if (!analysis || Object.keys(analysis).length === 0) return [];
  
  setProcessingStage('Extracting keywords...');
  
  const keywords = [];
  let filteredCount = 0;
  
  // Extract all keywords from analysis object
  Object.entries(analysis).forEach(([pageUrl, pageData]) => {
    if (!pageData.d) return; // Skip if no data array
    
    pageData.d.forEach(keywordEntry => {
      const keyword = keywordEntry.g; // keyword text
      const stats = keywordEntry.s || {};
      
      // Calculate total impressions across both periods
      const totalImpressions = (stats.i1 || 0) + (stats.i2 || 0);
      
      // Filter out keywords with fewer than 60 total impressions for better clustering
      if (totalImpressions < 50) {
        filteredCount++;
        return;
      }
      
      // Create keyword object compatible with clustering algorithm
      keywords.push({
        keyword: keyword,
        pageUrl: pageUrl,
        stats: {
          clicks1: stats.c1 || 0,
          clicks2: stats.c2 || 0,
          impr1: stats.i1 || 0,
          impr2: stats.i2 || 0,
          position1: stats.p1 || 0,
          position2: stats.p2 || 0,
          ctr1: stats.x1 || 0,
          ctr2: stats.x2 || 0,
          positionChange: stats.pc || 0
        },
        intent: keywordEntry.intent || null
      });
    });
  });
  
  if (filteredCount > 0) {
    console.log(`📊 Topic Analysis: Filtered out ${filteredCount.toLocaleString()} keywords with <20 impressions`);
  }
  
  setProcessingStage('');
  return keywords;
}

/**
 * Deduplicate and aggregate keywords that appear on multiple pages
 * @param {Array} keywordsForClustering - Array of keywords from multiple pages
 * @returns {Array} Deduplicated keywords with aggregated stats
 */
export function deduplicateKeywords(keywordsForClustering) {
  const keywordMap = new Map();
  
  keywordsForClustering.forEach(kw => {
    const keyword = kw.keyword;
    
    if (keywordMap.has(keyword)) {
      // Aggregate stats for existing keyword
      const existing = keywordMap.get(keyword);
      existing.clicks += kw.stats?.clicks2 || 0;
      existing.impressions += kw.stats?.impr2 || 0;
      existing.totalPosition += kw.stats?.position2 || 0;
      existing.positionCount += (kw.stats?.position2 || 0) > 0 ? 1 : 0;
      existing.pages.push(kw.pageUrl);
    } else {
      // New keyword
      keywordMap.set(keyword, {
        keyword: keyword,
        clicks: kw.stats?.clicks2 || 0,
        impressions: kw.stats?.impr2 || 0,
        totalPosition: kw.stats?.position2 || 0,
        positionCount: (kw.stats?.position2 || 0) > 0 ? 1 : 0,
        pages: [kw.pageUrl]
      });
    }
  });
  
  // Format keywords for backend API with averaged positions
  return Array.from(keywordMap.values()).map(kw => ({
    keyword: kw.keyword,
    clicks: kw.clicks,
    impressions: kw.impressions,
    position: kw.positionCount > 0 ? kw.totalPosition / kw.positionCount : 0,
    ctr: kw.impressions > 0 ? (kw.clicks / kw.impressions) * 100 : 0,
    pageCount: kw.pages.length
  }));
}

/**
 * Generate cache key hash for topic analysis (simplified approach)
 * @param {Object} meta - Analysis metadata with domain and date ranges
 * @returns {string} Cache key hash
 */
export function generateTopicCacheKey(meta) {
  // Create simple cache data structure based on property and date ranges
  const cacheData = {
    property_url: meta?.domain || '',
    period_one_start: meta?.dateRange?.periodOne?.startDate || '',
    period_one_end: meta?.dateRange?.periodOne?.endDate || '',
    period_two_start: meta?.dateRange?.periodTwo?.startDate || '',
    period_two_end: meta?.dateRange?.periodTwo?.endDate || ''
  };
  
  // Generate hash
  const cacheString = JSON.stringify(cacheData);
  console.log(`🔍 Cache key data:`, cacheData);
  return md5(cacheString);
}

/**
 * Check if topic analysis is cached
 * @param {Object} meta - Analysis metadata with domain and date ranges
 * @returns {Promise<Object|null>} Cached topics or null if not found
 */
export async function checkTopicAnalysisCache(meta) {
  try {
    const requestData = {
      property_url: meta?.domain || '',
      period_one_start: meta?.dateRange?.periodOne?.startDate || '',
      period_one_end: meta?.dateRange?.periodOne?.endDate || '',
      period_two_start: meta?.dateRange?.periodTwo?.startDate || '',
      period_two_end: meta?.dateRange?.periodTwo?.endDate || ''
    };
    
    const response = await apiRequest('/api/analyze/v2/check-topic-cache/', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
    
    const data = await response.json();
    
    if (data.cached) {
      console.log(`⚡ Topic Analysis: Cache HIT - returning cached results instantly`);
      return data.topics;
    } else {
      console.log(`🔄 Topic Analysis: Cache MISS - will run ML clustering`);
      return null;
    }
  } catch (error) {
    console.warn('Cache check failed, proceeding with ML clustering:', error);
    return null;
  }
}

/**
 * Perform ML topic clustering via backend API
 * @param {Array} keywordsForAPI - Deduplicated keywords for API
 * @param {Object} meta - Analysis metadata
 * @param {Array} keywordsForClustering - Original keywords for clustering
 * @param {boolean} skipCacheCheck - Skip cache check (when we know it's a miss)
 * @returns {Promise<Array>} Enhanced topics array
 */
export async function performMLClustering(keywordsForAPI, meta, keywordsForClustering, skipCacheCheck = false) {
  console.log(`🧠 Starting ML topic analysis...`);
  console.log(`📊 Original keyword entries: ${keywordsForClustering.length}`);
  console.log(`📊 Deduplicated keywords: ${keywordsForAPI.length}`);
  console.log(`📊 Deduplication saved: ${keywordsForClustering.length - keywordsForAPI.length} duplicates`);
  const startTime = performance.now();
  
  let data;
  let processingTime;
  let cachedTopics = null;
  
  if (!skipCacheCheck) {
    // Step 1: Check cache first (lightweight request)
    const cacheKeyHash = generateTopicCacheKey(meta);
    console.log(`🔑 Generated cache key hash: ${cacheKeyHash}`);
    cachedTopics = await checkTopicAnalysisCache(meta);
    
    if (cachedTopics) {
      // Use cached results
      data = { topics: cachedTopics };
      processingTime = Math.round(performance.now() - startTime);
    }
  }
  
  if (!cachedTopics) {
    // Step 2: Run expensive ML clustering
    const requestBody = {
      keywords: keywordsForAPI,
      property_url: meta?.domain || '', // Send domain for intent lookup
      // Add cache key data for backend to store with simplified key
      cache_key_data: {
        property_url: meta?.domain || '',
        period_one_start: meta?.dateRange?.periodOne?.startDate || '',
        period_one_end: meta?.dateRange?.periodOne?.endDate || '',
        period_two_start: meta?.dateRange?.periodTwo?.startDate || '',
        period_two_end: meta?.dateRange?.periodTwo?.endDate || ''
      }
    };
    
    const response = await apiRequest('/api/analyze/pages/keyword-topics/', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    const rawText = await response.text();
    
    try {
      data = JSON.parse(rawText);
    } catch (parseError) {
      throw new Error(`Invalid JSON response: ${rawText.substring(0, 100)}...`);
    }
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    processingTime = Math.round(performance.now() - startTime);
  }
  
  // Convert backend topics to frontend format
  const enhancedTopics = data.topics.map((topic, index) => ({
    id: index,
    topic: topic.name,
    topicName: topic.name,
    keywordCount: topic.keywords_count,
    totalClicks: topic.clicks,
    totalImpressions: topic.impressions,
    averagePosition: topic.keywords.reduce((sum, kw) => sum + (kw.position || 0), 0) / topic.keywords.length,
    members: topic.keywords.map(kw => {
      // Find all pages for this keyword
      const keywordPages = keywordsForClustering.filter(k => k.keyword === kw.keyword);
      const primaryPage = keywordPages.length > 0 ? keywordPages[0].pageUrl : '';
      
      return {
        keyword: kw.keyword,
        pageUrl: primaryPage,
        pages: keywordPages.map(k => k.pageUrl), // Store all pages for this keyword
        stats: {
          clicks2: kw.clicks,
          impr2: kw.impressions,
          position2: kw.position,
          ctr2: kw.ctr
        },
        intent: kw.intent // Intent comes from the API response
      };
    }),
    topPages: getTopPagesForTopic(topic, keywordsForClustering),
    intentDistribution: getIntentDistributionForTopic(topic)
  }));
  
  // Count keywords with intents from the API response
  const allKeywordsFromAPI = data.topics.flatMap(topic => topic.keywords);
  const keywordsWithIntents = allKeywordsFromAPI.filter(kw => kw.intent && kw.intent !== 'other').length;
  
  const wasCached = cachedTopics !== null;
  
  console.log(`🧠 ML Topic Analysis Complete:
    Keywords: ${keywordsForAPI.length.toLocaleString()}
    Topics: ${enhancedTopics.length}
    Processing Time: ${processingTime}ms
    Method: Sentence Transformers + K-means
    ${wasCached ? '⚡ (loaded from cache)' : '🔄 (fresh computation)'}
    🏷️ Keywords with intents: ${keywordsWithIntents}/${allKeywordsFromAPI.length} (${((keywordsWithIntents/allKeywordsFromAPI.length)*100).toFixed(1)}%)`);
  
  return enhancedTopics;
}

/**
 * Perform fallback clustering using frontend similarity
 * @param {Array} keywordsForClustering - Keywords to cluster
 * @returns {Array} Enhanced clusters array
 */
export function performFallbackClustering(keywordsForClustering) {
  const startTime = performance.now();
  const clusters = groupSimilarKeywords(keywordsForClustering, 0.6);
  
  const enhancedClusters = clusters.map((cluster, index) => ({
    ...cluster,
    id: index,
    keywordCount: cluster.members.length,
    averagePosition: cluster.members.reduce((sum, kw) => sum + (kw.stats?.position2 || 0), 0) / cluster.members.length,
    topPages: getTopPagesForCluster(cluster),
    intentDistribution: getIntentDistribution(cluster)
  })).sort((a, b) => b.totalClicks - a.totalClicks);
  
  const endTime = performance.now();
  const processingTime = Math.round(endTime - startTime);
  
  console.log(`🔄 Fallback Topic Analysis:
    Keywords: ${keywordsForClustering.length.toLocaleString()}
    Topics: ${enhancedClusters.length}
    Processing Time: ${processingTime}ms
    Method: Frontend semantic similarity`);
  
  return enhancedClusters;
}

/**
 * Get top pages for a cluster (frontend clustering)
 * @param {Object} cluster - The cluster object
 * @returns {Array} Top pages with traffic stats
 */
export function getTopPagesForCluster(cluster) {
  const pageTraffic = {};
  
  cluster.members.forEach(keyword => {
    const page = keyword.pageUrl;
    if (!pageTraffic[page]) {
      pageTraffic[page] = { clicks: 0, impressions: 0, keywords: 0 };
    }
    pageTraffic[page].clicks += keyword.stats?.clicks2 || 0;
    pageTraffic[page].impressions += keyword.stats?.impr2 || 0;
    pageTraffic[page].keywords += 1;
  });
  
  return Object.entries(pageTraffic)
    .sort((a, b) => b[1].clicks - a[1].clicks)
    .slice(0, 3)
    .map(([page, stats]) => ({ page, ...stats }));
}

/**
 * Get intent distribution for a cluster (frontend clustering)
 * @param {Object} cluster - The cluster object
 * @returns {Object} Intent distribution stats
 */
export function getIntentDistribution(cluster) {
  const intents = {};
  let totalWithIntent = 0;
  
  cluster.members.forEach(keyword => {
    if (keyword.intent) {
      intents[keyword.intent] = (intents[keyword.intent] || 0) + 1;
      totalWithIntent++;
    }
  });
  
  return { intents, totalWithIntent, total: cluster.members.length };
}

/**
 * Get top pages for ML clustering topics
 * @param {Object} topic - The topic object from ML clustering
 * @param {Array} keywordsForClustering - Original keywords for clustering
 * @returns {Array} Top pages with traffic stats
 */
export function getTopPagesForTopic(topic, keywordsForClustering) {
  const pageTraffic = {};
  
  topic.keywords.forEach(keyword => {
    const originalKeyword = keywordsForClustering.find(k => k.keyword === keyword.keyword);
    const page = originalKeyword?.pageUrl || '';
    
    if (page && !pageTraffic[page]) {
      pageTraffic[page] = { clicks: 0, impressions: 0, keywords: 0 };
    }
    
    if (page) {
      pageTraffic[page].clicks += keyword.clicks || 0;
      pageTraffic[page].impressions += keyword.impressions || 0;
      pageTraffic[page].keywords += 1;
    }
  });
  
  return Object.entries(pageTraffic)
    .sort((a, b) => b[1].clicks - a[1].clicks)
    .slice(0, 3)
    .map(([page, stats]) => ({ page, ...stats }));
}

/**
 * Get intent distribution for ML topics
 * @param {Object} topic - The topic object from ML clustering
 * @returns {Object} Intent distribution stats
 */
export function getIntentDistributionForTopic(topic) {
  const intents = {};
  let totalWithIntent = 0;
  
  topic.keywords.forEach(keyword => {
    if (keyword.intent && keyword.intent !== 'other') {
      intents[keyword.intent] = (intents[keyword.intent] || 0) + 1;
      totalWithIntent++;
    }
  });
  
  return { intents, totalWithIntent, total: topic.keywords.length };
}

/**
 * Calculate summary statistics for topic groups
 * @param {Array} topicGroups - Array of topic groups
 * @param {Array} keywordsForClustering - Original keywords for clustering
 * @returns {Object} Summary statistics object
 */
export function calculateSummaryStats(topicGroups, keywordsForClustering) {
  if (!topicGroups.length) return null;
  
  const totalKeywords = keywordsForClustering.length;
  const totalClusters = topicGroups.length;
  const averageClusterSize = totalKeywords / totalClusters;
  const largestCluster = Math.max(...topicGroups.map(c => c.keywordCount));
  const totalTraffic = topicGroups.reduce((sum, cluster) => sum + cluster.totalClicks, 0);
  
  return {
    totalKeywords,
    totalClusters,
    averageClusterSize: Math.round(averageClusterSize * 10) / 10,
    largestCluster,
    totalTraffic,
    clusteredKeywords: topicGroups.reduce((sum, cluster) => sum + cluster.keywordCount, 0),
    clusteringRatio: Math.round((topicGroups.length / totalKeywords) * 100)
  };
}

/**
 * Get the top intent for a cluster
 * @param {Object} cluster - The cluster object
 * @returns {Object|null} Top intent with count or null
 */
export function getTopIntent(cluster) {
  if (!cluster.intentDistribution.intents || Object.keys(cluster.intentDistribution.intents).length === 0) {
    return null;
  }
  
  const intentEntries = Object.entries(cluster.intentDistribution.intents);
  if (intentEntries.length === 0) return null;
  
  // Find the intent with the highest count
  const topIntent = intentEntries.reduce((max, current) => 
    current[1] > max[1] ? current : max
  );
  
  return {
    intent: topIntent[0],
    count: topIntent[1]
  };
} 